import { Component, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { SidebarService } from '../../../services/sidebar.service';
import { ApplicationStateService } from '../../../services/application-state.service';
import { BookFeatureActivationService } from '../../../services/book-feature-activation.service';
import { sidebarConfig } from '../../../../environments/configs/sidebar-config';
import { SidebarChangedEvent } from '../../../utils/events/sidebar-changed-event';
import { Subscription } from 'rxjs';
import { ModalService } from '../../../services/modal.service';
import { HomeworkService } from '../../../services/homework.service';
import { HomeworkFormComponent } from '../tasks/homework/homework-form/homework-form.component';
import { AbstractSidebarComponent } from './abstract-sidebar-component';
import { QuickLinkService } from '../../../services/quick-link.service';
import $ from 'jquery';
import _ from 'lodash';
import { QuickLink } from '../../../models/quick-link';
import { RouteService } from '../../../services/route.service';
import { SidebarContentComponent } from './sidebar-content-component';
import { AssetPipe } from '../../../pipes/asset.pipe';
import { BackgroundImageDirective } from '../../../directives/background-image.directive';
import { AsyncPipe, NgStyle } from '@angular/common';
import { UserMenuComponent } from '../user-menu/user-menu.component';
import { TranslationPipe } from '../../../pipes/translation.pipe';
import { TasksComponent } from '../tasks/tasks.component';
import { AccessibilityComponentDirective } from '../../../directives/accessibility-component.directive';
import { BookFeatureService } from '../../../services/book-feature.service';

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    standalone: true,
    imports: [
        AssetPipe,
        BackgroundImageDirective,
        NgStyle,
        UserMenuComponent,
        TranslationPipe,
        AsyncPipe,
        AccessibilityComponentDirective
    ]
})
export class SidebarComponent extends AbstractSidebarComponent implements OnInit, OnDestroy {

    sidebarConfig = sidebarConfig;
    showFAQButton: boolean;
    faqURL: string;

    private sidebarEventSubscription: Subscription;
    
    private childHost: ViewContainerRef;
    @ViewChild('childHost', {read: ViewContainerRef})
    set childHostSetter(vcr: ViewContainerRef) {
        if (!vcr) {
            return;
        }

        this.childHost = vcr;
        this.childHost.clear();
        this.childHost.createComponent(this.sidebarService.activeComponent as any);
    }

    constructor(public applicationStateService: ApplicationStateService,
                public quickLinkService: QuickLinkService,
                public bookFeatureActivationService: BookFeatureActivationService,
                public sidebarService: SidebarService,
                private routeService: RouteService,
                protected homeworkService: HomeworkService,
                public modalService: ModalService,
                public bookFeatureService: BookFeatureService,
                public quicklinkService: QuickLinkService) {

        super();
        this.sidebarEventSubscription = this.sidebarService.sidebarEventFired$.subscribe(event => {
            if (event.openSidebar) {
                this.openSidebar(event);
            } else {
                this.closeSidebar();
            }
        });
    }

    ngOnDestroy(): void {
        this.sidebarEventSubscription.unsubscribe();
    }

    ngOnInit(): void {
        this.showFAQButton = true;
        this.faqURL = this.routeService.getAbsoluteRoute('faq', {});

        const hasAMLinks = this.quickLinkService.quickLinks.digiFitQuicklinks.size > 0 ||
            this.quickLinkService.quickLinks.hsmQuickLinks.size > 0;
        this.homeworkService.showHomeworkButton = hasAMLinks &&
            this.applicationStateService.bookConfig.hsmQuicklinks;

        if (this.homeworkService.showHomeworkButton) {
            return;
        }

        if (this.quickLinkService.quickLinks.genericQuickLinks.size === 0) {
            return;
        }

        const bookPrefix = this.applicationStateService.bookConfig.quickLinkPrefix?.toLowerCase();
        if (!bookPrefix) {
            this.homeworkService.showHomeworkButton = false;

            return;
        }

        const quickLinks = this.quickLinkService.quickLinks.flatten(this.quickLinkService.quickLinks.genericQuickLinks);
        const prefixedQuickLinks = _.find(quickLinks, (element: QuickLink) => {

            const platformDomain = this.applicationStateService.platformDomain;
            const lowerCasePrefix = this.applicationStateService.bookConfig.quickLinkPrefix.toLowerCase();

            return (element.slug && element.slug.toLowerCase().startsWith(bookPrefix)) ||
                (element.link && element.link.toLowerCase()
                    .startsWith(`${platformDomain}/${lowerCasePrefix}`));
        });

        this.homeworkService.showHomeworkButton = prefixedQuickLinks !== undefined;
    }

    async openComponentInSidebar(sidebarContentComponent: SidebarContentComponent | string): Promise<void> {
        if (!await this.homeworkService.checkAlert()) {
            return;
        }

        super.openComponentInSidebar(sidebarContentComponent);
    }

    async close(): Promise<void> {
        if (!await this.homeworkService.checkAlert()) {
            return;
        }
        this.sidebarService.triggerSidebarChangeEvent(new SidebarChangedEvent(false));
    }

    stopPropagation($event: UIEvent): void {
        $event.stopPropagation();
    }

    openSidebar(sidebarChangedEvent: SidebarChangedEvent): void {
        this.applicationStateService.isSidebarHidden = sidebarChangedEvent.closeSidebar;
        if (sidebarChangedEvent.openSidebar) {
            this.loadChildComponent(sidebarChangedEvent.component);
        } else {
            this.sidebarService.triggerSidebarChangeEvent(new SidebarChangedEvent(false));
        }
    }

    private closeSidebar(): void {
        this.sidebarService.activeComponent = undefined;
        this.homeworkService.deactivateHomework();
        this.applicationStateService.isSidebarHidden = true;
        this.homeworkService.testGeneratorOpened = false;
    }

    private loadChildComponent(component): void {

        if (component === null) {
            return;
        }

        if (component === TasksComponent) {
            document.querySelector('.sidebar-content-wrapper').classList.add('no-padding');
        } else {
            document.querySelector('.sidebar-content-wrapper').classList.remove ('no-padding');
        }

        if (component !== HomeworkFormComponent) {
            this.homeworkService.deactivateHomework();
        }

        this.sidebarService.activeComponent = component;
        if (this.childHost) {
            this.childHost.clear();
            this.childHost.createComponent(component);
        }
        if (component === this.PagesContentComponent) {
            this.scrollToActivePage();
        }
    }

    private scrollToActivePage(): void {
        setTimeout(() => {
            const element = $('.preview-container.active').first();

            if (element && element.offset()) {
                const container = $('.sidebar-content-wrapper');
                container.stop();
                const offset = element.offset().top - container.offset().top + container.scrollTop();
                container.animate({
                    scrollTop: offset - 10 - container.height() / 2 + element.height() / 2
                }, 1000);
            }
        }, 50);
    }
}
